import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ZLevels } from '../../sizes';

interface DropdownPortalProps {
    /** The trigger element ref to position relative to */
    triggerRef: React.RefObject<HTMLElement>;
    /** Whether the dropdown is open */
    isOpen: boolean;
    /** Callback when clicking outside */
    onClose: () => void;
    /** The dropdown content */
    children: React.ReactNode;
    /** Width of the dropdown - 'trigger' matches trigger width, or provide pixel value */
    width?: 'trigger' | number;
    /** Max height of the dropdown */
    maxHeight?: number;
}

/**
 * Portal-based dropdown that renders at document.body level to escape container clipping.
 * Automatically positions below the trigger element.
 */
export const DropdownPortal: React.FC<DropdownPortalProps> = ({
    triggerRef,
    isOpen,
    onClose,
    children,
    width = 'trigger',
    maxHeight = 240,
}) => {
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Update position when open or on scroll/resize
    useEffect(() => {
        if (!isOpen || !triggerRef.current) return;

        const updatePosition = () => {
            const rect = triggerRef.current!.getBoundingClientRect();
            const dropdownHeight = maxHeight;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            // Flip above the trigger if not enough room below
            const showAbove = spaceBelow < dropdownHeight + 8 && spaceAbove > spaceBelow;
            const computedWidth = width === 'trigger' ? rect.width : width;
            // Clamp left so the dropdown doesn't overflow the right edge
            const maxLeft = window.innerWidth - (typeof computedWidth === 'number' ? computedWidth : rect.width) - 8;
            setPosition({
                top: showAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
                left: Math.min(rect.left, maxLeft),
                width: computedWidth,
            });
        };

        updatePosition();

        // Update on scroll/resize
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, triggerRef, width]);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                triggerRef.current &&
                !triggerRef.current.contains(target)
            ) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose, triggerRef]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div
            ref={dropdownRef}
            className={`fixed bg-white rounded-lg border border-[#a1a1b6] shadow-xl overflow-y-auto ${ZLevels.topMost}`}
            style={{
                top: position.top,
                left: position.left,
                width: typeof position.width === 'number' ? position.width : undefined,
                maxHeight,
            }}
        >
            {children}
        </div>,
        document.body
    );
};

export default DropdownPortal;
